/**
 * Login Page - Supabase Email/Password Auth
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TacitLogo } from '@/components/ui/Logo';
import { useToast } from '@/hooks/use-toast';
import { sanitizeInternalReturnPath } from '@/lib/auth/return-to';
import { supabase } from '@/lib/supabase';
import { toSafeUiError } from '@/lib/user-safe-error';

export default function Login(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const loginInFlightRef = useRef(false);

  useEffect(() => {
    document.title = 'Tacit CRM - Strategic Context Engine';
  }, []);

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (loginInFlightRef.current) {
      return;
    }
    loginInFlightRef.current = true;
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        toast({
          title: 'Welcome back!',
          description: 'You are now logged in.',
        });
        navigate(
          sanitizeInternalReturnPath(searchParams.get('returnTo')),
          { replace: true },
        );
      }
    } catch (error: unknown) {
      const safeError = toSafeUiError(error, 'auth');
      toast({
        title: 'Login failed',
        description: `${safeError.description} ${safeError.recovery}`,
        variant: 'destructive',
      });
    } finally {
      loginInFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-elevation-3">
        <CardHeader className="space-y-1">
          <div className="mb-4 flex items-center gap-2">
            <TacitLogo className="h-10 w-10" />
            <div>
              <h1 className="text-2xl font-bold leading-none tracking-tight">
                Tacit CRM
              </h1>
              <CardDescription className="text-xs">Strategic Context Engine</CardDescription>
            </div>
          </div>
          <CardDescription>
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
